/**
 * Inline editor for the automation template name.
 * Displays the name with a pencil icon to toggle edit mode; in edit mode,
 * shows an input with Save/Cancel buttons.
 */

import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useAutomationTemplateSetNameMutation } from '../../../queries/Automation/mutations/template';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { EditItemPenIcon } from '../../../web/components/svgs/icons/EditItemPenIcon';
import { darkGrayFontColor, shuffleGray, titles } from '../../../web/styles/colors';

const styleSheet = StyleSheet.create({
	title: {
		fontSize: 26,
		fontWeight: 600,
		color: titles,
		margin: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	titleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		minWidth: 0,
	},
	editIconButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: THEME.spacing.xs,
		backgroundColor: 'transparent',
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		cursor: 'pointer',
		color: darkGrayFontColor,
		flexShrink: 0,
		':hover': {
			backgroundColor: shuffleGray,
			color: titles,
		},
	},
	nameEditRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flex: 1,
		minWidth: 0,
	},
	nameInput: {
		flex: 1,
		minWidth: 0,
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: 600,
	},
});

export interface AutomationTitleEditorProps {
	template: Api.IAutomationTemplate | undefined;
	impersonationContext?: Api.IImpersonationContext;
	/** When true, the pencil icon is hidden (e.g. when viewing published version). */
	isPublishedPreview?: boolean;
	/** When true, name editing is disabled regardless of preview state. */
	readOnly?: boolean;
	defaultName?: string;
}

export function AutomationTitleEditor({
	template,
	impersonationContext,
	isPublishedPreview = false,
	readOnly = false,
	defaultName = 'Automation Flow Builder',
}: AutomationTitleEditorProps) {
	const errorMessages = useErrorMessages();
	const [isEditingName, setIsEditingName] = useState(false);
	const [editName, setEditName] = useState(template?.name ?? '');

	const { mutate: setNameMutation, isPending: isSavingName } = useAutomationTemplateSetNameMutation({
		onSuccess: () => {
			toast.success('Automation name saved.');
			setIsEditingName(false);
		},
		onError: err => {
			errorMessages.pushApiError(err);
		},
	});

	useEffect(() => {
		if (!isEditingName && template?.name !== undefined) {
			setEditName(template.name);
		}
	}, [template?.name, isEditingName]);

	const handlePencilClick = () => {
		setEditName(template?.name ?? '');
		setIsEditingName(true);
	};

	const handleSaveName = () => {
		const trimmed = editName?.trim();
		if (!trimmed || !template?.id) {
			setIsEditingName(false);
			setEditName(template?.name ?? '');
			return;
		}
		if (trimmed === template?.name) {
			setIsEditingName(false);
			return;
		}
		setNameMutation({
			id: template.id,
			name: trimmed,
			impersonationContext,
		});
	};

	const handleCancelEditName = () => {
		setEditName(template?.name ?? '');
		setIsEditingName(false);
	};

	const handleNameInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSaveName();
		}
		if (e.key === 'Escape') {
			e.preventDefault();
			handleCancelEditName();
		}
	};

	if (isEditingName) {
		return (
			<div className={css(styleSheet.nameEditRow)}>
				<Input
					autoFocus
					styles={[styleSheet.nameInput]}
					type='text'
					value={editName}
					onChange={e => setEditName(e.target.value)}
					onKeyDown={handleNameInputKeyDown}
				/>
				<Button variant='primary' size='sm' onClick={handleSaveName} disabled={isSavingName}>
					Save
				</Button>
				<Button variant='secondary' size='sm' onClick={handleCancelEditName} disabled={isSavingName}>
					Cancel
				</Button>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.titleRow)}>
			<h1 className={css(styleSheet.title)}>{template?.name || defaultName}</h1>
			{!isPublishedPreview && !readOnly && (
				<button
					className={css(styleSheet.editIconButton)}
					onClick={handlePencilClick}
					title='Edit automation name'
					type='button'
					aria-label='Edit automation name'
				>
					<EditItemPenIcon />
				</button>
			)}
		</div>
	);
}
